import { useState } from 'react';
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	CircularProgress,
	Alert,
	TablePagination,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { useGetList } from 'react-admin';
import { circleLogSummary, logActionText, platformText } from './activityLogLabels';

type Filter = { userId?: string; companyId?: string; action?: string; actionCategory?: string; entityId?: string };

type Props = {
	open: boolean;
	onClose: () => void;
	filter: Filter;
	filterLabel: string;
};

const FilteredLogsDialog = ({ open, onClose, filter, filterLabel }: Props) => {
	const hasFilter = Boolean(filter.userId || filter.companyId || filter.action || filter.actionCategory || filter.entityId);
	const [page, setPage] = useState(0);
	const perPage = 20;

	const { data, total, isLoading, error } = useGetList(
		'activity-logs',
		{
			filter,
			pagination: { page: page + 1, perPage },
			sort: { field: 'timestamp', order: 'DESC' },
		},
		{ enabled: open && hasFilter }
	);

	const handleClose = () => {
		setPage(0);
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth dir="rtl">
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<Typography variant="h6">לוגים: {filterLabel}</Typography>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{!hasFilter && open && (
					<Alert severity="info">לא נבחר סינון</Alert>
				)}
				{error && (
					<Alert severity="error">שגיאה בטעינת הלוגים</Alert>
				)}
				{isLoading && (
					<Box display="flex" justifyContent="center" py={4}>
						<CircularProgress />
					</Box>
				)}
				{hasFilter && data && !isLoading && (
					<TableContainer>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell align="center">תאריך</TableCell>
									<TableCell align="right">משתמש</TableCell>
									<TableCell align="right">חברה</TableCell>
									<TableCell align="right">פעולה</TableCell>
									<TableCell align="right">פלטפורמה</TableCell>
									<TableCell align="right">מטא-דאטה</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} align="center">
											אין רשומות
										</TableCell>
									</TableRow>
								) : (
									data.map((row: any) => (
										<TableRow key={row.id ?? row._id}>
											<TableCell align="center">
												{row.timestamp
													? moment(row.timestamp).format('DD/MM/YYYY HH:mm')
													: '-'}
											</TableCell>
											<TableCell align="right">{row.userName ?? '-'}</TableCell>
											<TableCell align="right">{row.companyName ?? '-'}</TableCell>
											<TableCell align="right">{logActionText(row) || '-'}</TableCell>
											<TableCell align="right">{platformText(row.platform)}</TableCell>
											<TableCell align="right">
												{circleLogSummary(row) ||
												(row.metadata && typeof row.metadata === 'object' && Object.keys(row.metadata).length > 0
													? (() => {
															try {
																const str = JSON.stringify(row.metadata);
																return str.length > 60 ? str.slice(0, 60) + '…' : str;
															} catch {
																return '-';
															}
														})()
													: row.entityId
														? row.entityType
															? `${row.entityType}: ${row.entityId}`
															: row.entityId
														: '-')}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
						<TablePagination
							component="div"
							count={total ?? 0}
							page={page}
							onPageChange={(_, newPage) => setPage(newPage)}
							rowsPerPage={perPage}
							rowsPerPageOptions={[perPage]}
							labelDisplayedRows={({ from, to, count }) =>
								`${from}-${to} מתוך ${count !== -1 ? count : `יותר מ-${to}`}`
							}
						/>
					</TableContainer>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default FilteredLogsDialog;
